import React from 'react'
import Grid from '@material-ui/core/Grid'
import styled from 'styled-components'
import {Draggable, DraggableProvidedDraggableProps} from 'react-beautiful-dnd'
import CountDownTimer, {
  CountDownTimerSettings,
} from 'Event/Dashboard/components/CountDownTimer'
import {Editable} from 'Event/Dashboard/editor/views/EditComponent'
import {useEditMode} from 'Event/EditModeProvider'
import {DraggableOverlay} from 'lib/ui/drag-and-drop'
import {useToggle} from 'lib/toggle'
import {now, date} from 'lib/date-time'
import CountDownTimerConfig from 'Event/Dashboard/components/CountDownTimer/CountDownTimerConfig'
import {useDuplicateCountDownTimer} from 'Event/Dashboard/components/CountDownTimer/duplicate-countdown-timer'
import {useSimpleBlogTemplate} from 'Event/template/SimpleBlog'

type CountDownTimerProps = {
  id: string
  countDownTimer: CountDownTimerSettings
  index: number
}

export default React.memo((props: CountDownTimerProps) => {
  const {flag: configVisible, toggle: toggleConfig} = useToggle()
  const isEditMode = useEditMode()
  const {countDownTimers, localization} = useSimpleBlogTemplate()
  const duplicate = useDuplicateCountDownTimer({countDownTimers, localization})

  const timer = <CountDownTimer {...props.countDownTimer} id={props.id} />

  if (!isEditMode) {
    return <LiveTimer {...props}>{timer}</LiveTimer>
  }

  return (
    <>
      <CountDownTimerConfig
        showing={configVisible}
        onClose={toggleConfig}
        id={props.id}
        keyPath={`countDownTimers.${props.id}`}
        countDownTimer={props.countDownTimer}
      />
      <Draggable draggableId={props.id} index={props.index}>
        {(provided) => (
          <CountDownTimerContainer
            ref={provided.innerRef}
            draggableProps={provided.draggableProps}
          >
            <DraggableOverlay>
              <Editable
                onEdit={toggleConfig}
                onCopy={() => duplicate(props.id)}
                handleProps={provided.dragHandleProps}
              >
                <>{timer}</>
              </Editable>
            </DraggableOverlay>
          </CountDownTimerContainer>
        )}
      </Draggable>
    </>
  )
})

function LiveTimer(
  props: CountDownTimerProps & {children: React.ReactElement},
) {
  const current = now()
  const isAfter = date(props.countDownTimer.end).isAfter(current)

  if (!isAfter) {
    return null
  }

  return <CountDownTimerContainer>{props.children}</CountDownTimerContainer>
}

const CountDownTimerContainer = React.forwardRef<
  HTMLDivElement,
  {
    children: React.ReactElement
    draggableProps?: DraggableProvidedDraggableProps
  }
>((props, ref) => {
  return (
    <>
      <StyledGrid item xs={12} ref={ref} {...props.draggableProps}>
        {props.children}
      </StyledGrid>
    </>
  )
})

const StyledGrid = styled(Grid)`
  display: flex;
  align-items: center;
  justify-content: center;
`
